// The delayed, resettable presentation clock (design D8, UI.md §8) — the pure, GPU-free
// seam the RAF render loop (group 11) composes to smooth ~10 Hz streamed motion up to the
// display refresh. It buffers the two most-recent immutable tick samples per entity and
// renders each at a DELAYED presentation target of `newestTick − ~1 stream interval`,
// interpolating between those two samples via the (previously dead)
// `interpolate.ts:interpolatePosition` and clamping at the newest received tick so it NEVER
// extrapolates past it. The delay guarantees two samples always bracket the target, so a
// stalled stream degrades to a clamp rather than a guess.
//
// The per-entity sample history resets independently on three discontinuities so no position
// is interpolated across one:
//
//   1. a tier change (close↔wide) — the entity set changes;
//   2. a sequence gap OR connection epoch — both delivered through the render-input
//      `streamEpoch` token (bumped once per gap and once per reconnect, group 3.2), so the
//      clock watches that single monotonic counter;
//   3. a non-monotonic (replay-scrub) playhead — a backward seek.
//
// The decorative hover-bob runs on this SAME wall-clock presentation clock, DELIBERATELY
// separate from the replay playhead: it is a pure function of wall-clock `now` (plus a
// per-bee phase), so a paused / backward-seeking replay resets the interpolation history but
// never advances (or rewinds) bee bob motion.
//
// Pure and Pixi-free: `ingest` is fed once per accepted world frame with the injected clock's
// `now`, and `positionAt` / `hoverBob` are read per RAF frame; the GPU wiring is on-device
// only (D-086).

import { Tier } from '../net/wire/messages';
import { interpolatePosition, type TickSample } from './interpolate';

/** How far behind the newest received tick the presentation target sits, in ticks — ~1
 * close-tier stream interval (the ~10 Hz close stream is ~1.5 physics ticks apart). The
 * delay keeps two buffered samples bracketing the target so the renderer interpolates
 * rather than extrapolates. */
export const PRESENTATION_DELAY_TICKS = 1.5;

/** Hover-bob period (ms) — one full decorative bob cycle for a flying bee. */
export const HOVER_BOB_PERIOD_MS = 900;

/** Hover-bob amplitude (screen units) — the peak vertical decorative offset. */
export const HOVER_BOB_AMPLITUDE = 1.5;

/** One entity's position at one accepted tick — the minimal per-frame sample the clock
 * buffers for interpolation. */
export interface EntitySample {
  readonly id: number;
  readonly tick: number;
  readonly position: readonly [number, number];
}

/** The per-frame discontinuity tokens the clock resets its sample history on (design D8).
 * `tier` comes from the world snapshot; `streamEpoch` and `playhead` from the render-input
 * (group 3.2). `streamEpoch` covers BOTH a sequence gap and a connection epoch (reconnect),
 * since a reconnect bumps it too — so a single monotonic counter drives the history reset. */
export interface PresentationTokens {
  readonly tier: Tier;
  readonly streamEpoch: number;
  readonly playhead: number | null;
}

interface EntityHistory {
  /** The older of the two retained samples — the interpolation's `prev`. */
  prev: TickSample;
  /** The newest retained sample — the interpolation's `next`. Equal to `prev` after a
   * reset / first sight, so the position clamps to it (no interpolation across a
   * discontinuity) until a second post-reset frame lands. */
  next: TickSample;
}

export interface PresentationClockOptions {
  /** The presentation delay in ticks (default `PRESENTATION_DELAY_TICKS`). */
  readonly delayTicks?: number;
  /**
   * Wall-clock → tick conversion rate in Hz.
   *
   * **wire-required** (client-tick-rate-provenance): this surface runs after a handshake, so
   * the rate is a REQUIRED parameter sourced from `Welcome.tick_rate_hz` /
   * `manifest.tickRateHz` — no defaulted argument, no `?? 15`, and no import of the generated
   * `SIM_TICK_RATE_HZ` mirror. A caller with no rate to pass must fail to compile rather than
   * silently rendering against a build default that may not match the server it is connected
   * to.
   */
  readonly tickRateHz: number;
}

export class PresentationClock {
  private readonly delayTicks: number;
  private readonly tickRateHz: number;
  private playbackRate = 1;
  private readonly history = new Map<number, EntityHistory>();

  /** The newest accepted tick and the wall-clock instant it landed — the basis for the
   * delayed render target's wall-clock advance between frames. */
  private newestTick = 0;
  private newestAtMs = 0;

  private initialized = false;
  private lastTier: Tier | null = null;
  private lastStreamEpoch = -1;
  private lastPlayhead: number | null = null;

  constructor(options: PresentationClockOptions) {
    this.delayTicks = options.delayTicks ?? PRESENTATION_DELAY_TICKS;
    this.tickRateHz = options.tickRateHz;
  }

  /** Number of entities with buffered samples — exposed so a history reset can be asserted
   * directly (it drops to 0 when cleared, and re-grows only as fresh samples arrive). */
  get historySize(): number {
    return this.history.size;
  }

  /**
   * Consume one accepted world frame: reset the sample history first if any discontinuity
   * token changed (tier, `streamEpoch`, or a backward playhead), then buffer each present
   * entity's newest two samples and record the newest tick + its wall-clock instant.
   *
   * `newestTick` is the frame's tick (the freshest sample any entity carries); `nowMs` is the
   * injected clock's time at ingest, the basis the delayed render target advances from.
   */
  ingest(
    samples: Iterable<EntitySample>,
    newestTick: number,
    tokens: PresentationTokens,
    nowMs: number,
  ): void {
    // Captured before `adoptTokens` flips it: the first-ever ingest must anchor even though it
    // is not a reset and its tick may equal the `newestTick` field's initial 0 — which is the
    // literal first tick of a rounds trace.
    const first = !this.initialized;
    const reset = this.shouldReset(tokens);
    if (reset) {
      this.history.clear();
    }
    this.adoptTokens(tokens);

    for (const sample of samples) {
      const buffered: TickSample = {
        tick: sample.tick,
        position: [sample.position[0], sample.position[1]],
      };
      const existing = this.history.get(sample.id);
      if (!existing) {
        // First sight (or first post-reset frame): the sample stands alone, so the position
        // clamps to it — never interpolated across the discontinuity that cleared history.
        this.history.set(sample.id, { prev: buffered, next: buffered });
      } else if (buffered.tick > existing.next.tick) {
        // A newer tick: shift the window forward (drop the oldest).
        this.history.set(sample.id, { prev: existing.next, next: buffered });
      } else {
        // Same or a non-advancing tick within one frame: replace the newest in place rather
        // than corrupting the prev ≤ next ordering the interpolation relies on.
        existing.next = buffered;
      }
    }

    // **Anchor only when the presentation timeline actually moves.** `renderTickAt` advances
    // from `newestAtMs`, starting at `newestTick − delayTicks`, so re-anchoring on a tick
    // already accepted restarts the delay window and snaps every bee back by `delayTicks`.
    // That is not hypothetical: `CanvasRenderer.render()` ingests on every call, and it is
    // called for reasons that carry no new frame — a camera pan or zoom pushes the CURRENT
    // world through it again — so a drag used to stutter for its whole duration. A reset
    // always re-anchors, because after a discontinuity the old anchor describes a timeline
    // that no longer exists.
    if (first || reset || newestTick !== this.newestTick) {
      this.newestTick = newestTick;
      this.newestAtMs = nowMs;
    }
  }

  /** The delayed presentation render tick at wall-clock `nowMs`: it starts at
   * `newestTick − delayTicks` when the newest sample lands and advances toward the newest
   * tick with elapsed wall-clock, clamped at `newestTick` so the renderer never extrapolates
   * past the newest received sample. */
  renderTickAt(nowMs: number): number {
    const elapsedTicks =
      ((nowMs - this.newestAtMs) * this.tickRateHz * this.playbackRate) / 1000;
    const target = this.newestTick - this.delayTicks + elapsedTicks;
    return Math.min(target, this.newestTick);
  }

  /** Changes only wall-clock presentation speed. Re-anchors at the current target so the change
   * introduces neither a backward burst nor a stall; the fixed simulation tick rate remains the
   * constructor-owned provenance used by world-time trackers. */
  setPlaybackRate(rate: number, nowMs: number): void {
    if (!Number.isFinite(rate) || rate <= 0 || rate === this.playbackRate) return;
    const currentTarget = this.renderTickAt(nowMs);
    this.playbackRate = rate;
    const elapsedTicksFromAnchor = currentTarget - (this.newestTick - this.delayTicks);
    this.newestAtMs =
      nowMs - (elapsedTicksFromAnchor / (this.tickRateHz * this.playbackRate)) * 1000;
  }

  /** The interpolated position for entity `id` at the delayed presentation target, or
   * `undefined` if it has no buffered samples (never seen, or cleared by a reset). Pure —
   * reading never mutates the buffer. */
  positionAt(id: number, nowMs: number): [number, number] | undefined {
    const entity = this.history.get(id);
    if (!entity) {
      return undefined;
    }
    return interpolatePosition(entity.prev, entity.next, this.renderTickAt(nowMs));
  }

  /**
   * The decorative hover-bob vertical offset (screen units) for a flying bee at wall-clock
   * `nowMs` (design D8). A pure function of wall-clock time and a per-bee `phase` (e.g. its
   * id, to stagger the swarm) — DELIBERATELY independent of the replay playhead, so a paused
   * or backward-seeking replay never advances (or rewinds) the bob.
   */
  hoverBob(nowMs: number, phase = 0): number {
    const angle = (nowMs / HOVER_BOB_PERIOD_MS) * 2 * Math.PI + phase;
    return Math.sin(angle) * HOVER_BOB_AMPLITUDE;
  }

  /** True if any discontinuity token moved since the last accepted frame — a tier change, a
   * `streamEpoch` bump (gap or reconnect), or a backward (non-monotonic) playhead. The first
   * frame only adopts a baseline, never a reset. */
  private shouldReset(tokens: PresentationTokens): boolean {
    if (!this.initialized) {
      return false;
    }
    if (tokens.tier !== this.lastTier) {
      return true;
    }
    if (tokens.streamEpoch !== this.lastStreamEpoch) {
      return true;
    }
    if (
      tokens.playhead !== null &&
      this.lastPlayhead !== null &&
      tokens.playhead < this.lastPlayhead
    ) {
      return true;
    }
    return false;
  }

  private adoptTokens(tokens: PresentationTokens): void {
    this.initialized = true;
    this.lastTier = tokens.tier;
    this.lastStreamEpoch = tokens.streamEpoch;
    this.lastPlayhead = tokens.playhead;
  }
}
